import { ModalPanelProps } from '../DashboardModal';
import {
    Coordination,
    CoordinationState,
    CreateCoordinationRequest,
    DetailedSolution,
    SolutionState,
} from 'app/model/SOL49';
import React, { createRef, PureComponent } from 'react';
import TimeUtils from 'app/common/util/TimeUtils';
import { Button, Form, Input, notification, Select, Tag } from 'antd';
import { Flex } from 'app/components/common/Flex';
import InputTime from 'app/common/input/InputTime';
import { RefSelectProps } from 'antd/es/select';
import './ShareSolutionPanel.less';
import { ServiceFactory } from 'app/service/ServiceFactory';
import { SolutionUtils } from 'app/common/util/SolutionUtils';
import ConditionalRender from 'app/common/components/ConditionalRender';
import Utils from 'app/common/util/Utils';
import type { CustomTagProps } from 'rc-select/lib/BaseSelect';
import { CoordinationSummaryUpdateEvent } from '../../../common/eventbus/Events';
import AppContext from '../../../common/context/AppContext';
import TimePeriodUtils from '../../../common/util/TimePeriodUtils';

const { TextArea } = Input;

interface ShareSolutionPanelProps extends ModalPanelProps {
    solutions: DetailedSolution[];
    computedSolutions: DetailedSolution[];
    onShareSolution: () => void;
}

interface ShareSolutionPanelState {
    selectedSolutions: string[];
    concernedUnits: string[];
    fmpUnits: string[];
    responseTime: Date;
    comment: string;
}

export class ShareSolutionPanel extends PureComponent<ShareSolutionPanelProps, ShareSolutionPanelState> {
    private solutionsSelectRef = createRef<RefSelectProps>();
    private unitsSelectRef = createRef<RefSelectProps>();

    constructor(props) {
        super(props);

        this.state = {
            concernedUnits: this.getConcernedUnits(this.props.solutions),
            selectedSolutions: this.props.solutions.map(s => s.name),
            responseTime: TimeUtils.addMinutes(AppContext.getCurrentTime(), 60),
            comment: '',
            fmpUnits: [],
        };
    }

    componentDidMount() {
        ServiceFactory.sol49Service()
            .getFmpUnits()
            .then(reply => this.setState({ fmpUnits: reply.data.map(u => u.id) }))
            .catch(err => notification.error({ message: err }));
    }

    getConcernedUnits = (solutions: DetailedSolution[]) => {
        const impactedFMPs = solutions
            .flatMap(solution => SolutionUtils.getImpactedFMPs(solution))
            .filter(fmp => fmp !== 'UNKNOWN');
        return Utils.removeDuplicates(impactedFMPs);
    };

    onSelectedSolutionsChange = (solutions: string[]) => {
        this.solutionsSelectRef.current.blur();
        const selectedSolutions: DetailedSolution[] = this.props.computedSolutions.filter(s =>
            solutions.includes(s.name),
        );
        this.setState({
            selectedSolutions: solutions,
            concernedUnits: Utils.copyAndAddAllIfNeeded(
                this.state.concernedUnits,
                this.getConcernedUnits(selectedSolutions),
            ),
        });
    };

    onConcernedUnitsChange = (units: string[]) => {
        this.unitsSelectRef.current.blur();
        this.setState({
            concernedUnits: units,
        });
    };

    private onResponseTimeChange = (date: Date) => {
        return this.setState({ responseTime: TimeUtils.mergeDateAndTime(AppContext.getMonitoredDay(), date) });
    };

    onShare = () => {
        const solutions: DetailedSolution[] = this.props.computedSolutions.filter(s =>
            this.state.selectedSolutions.includes(s.name),
        );
        const coordinationStatus: { [key: string]: { [key: string]: SolutionState } } = {};
        this.state.concernedUnits.forEach(anuId => {
            const solutionStatus: { [key: string]: SolutionState } = {};
            solutions.forEach(s => {
                if (SolutionUtils.isInitialSolution(s)) {
                    // Initial solution, default state is Approved
                    const concernedFmps = this.getConcernedUnits([s]);
                    if (concernedFmps.includes(anuId)) {
                        solutionStatus[s.name] = SolutionState.APPROVED;
                    } else {
                        solutionStatus[s.name] = SolutionState.NO_RESPONSE;
                    }
                } else {
                    solutionStatus[s.name] = SolutionState.NO_RESPONSE;
                }
            });
            coordinationStatus[anuId] = solutionStatus;
        });

        const name = this.state.concernedUnits.map(unit => unit.replace('FMP', '')).join('_');
        const owner = AppContext.configData.claimedUserId;

        const coordination: Coordination = {
            owner: owner,
            solutions: solutions,
            name: name,
            state: CoordinationState.ONGOING,
            status: coordinationStatus,
            targetTime: this.state.responseTime,
        };

        const request: CreateCoordinationRequest = {
            coordination: coordination,
            comment: Utils.isNotEmptyString(this.state.comment) ? this.state.comment : null,
        };

        ServiceFactory.sol49Service()
            .createCoordination(request)
            .then(reply => {
                const coordinationSummary = {
                    id: reply.data,
                    owner: owner,
                    name: name,
                    solutions: solutions,
                    state: CoordinationState.ONGOING,
                    targetTime: this.state.responseTime,
                    unreadMessage: false,
                    status: coordinationStatus,
                };
                CoordinationSummaryUpdateEvent.fireEvent(coordinationSummary);
                this.props.onShareSolution();
                this.props.onClose();
            })
            .catch(err => {
                console.error('Error creating coordination !', err);
                notification.error({ message: 'Error creating coordination', description: '' + err });
            });
    };

    /* Overriding TagRender to customize the closable props */
    tagRender = (props: CustomTagProps) => {
        const { label, value, onClose } = props;
        const onPreventMouseDown = (event: React.MouseEvent<HTMLSpanElement>) => {
            event.preventDefault();
            event.stopPropagation();
        };
        const selectedSolutions: DetailedSolution[] = this.props.computedSolutions.filter(s =>
            this.state.selectedSolutions.includes(s.name),
        );
        return (
            <Tag color={value} onMouseDown={onPreventMouseDown} closable={true} onClose={onClose}>
                {label}
            </Tag>
        );
    };

    render() {
        const unselected = this.props.computedSolutions.filter(s => !this.state.selectedSolutions.includes(s.name));

        const initialSolution = SolutionUtils.getInitialSolution(this.props.solutions);
        const title = Utils.isExists(initialSolution) ? 'Share alternative solutions to:' : 'Share solutions:';

        const fmpOptions = this.state.fmpUnits
            .filter(unit => !this.state.concernedUnits.includes(unit))
            .sort()
            .map(unit => ({ value: unit }));
        const solOptions = unselected.map(sol => ({ value: sol.name }));

        const concernedUnits = this.state.concernedUnits.filter(unit => unit !== 'UNKNOWN');

        const timePeriod = {
            from: AppContext.getCurrentTime(),
            to: this.state.responseTime,
        };
        const isShareDisabled =
            Utils.isEmpty(this.state.selectedSolutions) ||
            Utils.isEmpty(concernedUnits) ||
            !TimePeriodUtils.isValid(timePeriod);
        return (
            <Flex className="share-solution" direction="vertical">
                <Flex className="header" direction="vertical" size={'3em'}>
                    <div className="title">{title}</div>
                    <ConditionalRender condition={Utils.isExists(initialSolution)}>
                        {initialSolution?.measures.map(m => (
                            <div key={m.measureId.id} className="name">
                                {m.measureId.id}
                            </div>
                        ))}
                    </ConditionalRender>
                </Flex>
                <Form layout="vertical">
                    <Form.Item className="solutions" label="Solutions">
                        <Select
                            ref={this.solutionsSelectRef}
                            mode="multiple"
                            onChange={this.onSelectedSolutionsChange}
                            notFoundContent="Not other computed solution available"
                            placeholder="Select computed solution(s)"
                            value={this.state.selectedSolutions}
                            options={solOptions}
                        />
                    </Form.Item>
                    <Form.Item className="recipients" label="Recipients">
                        <Select
                            ref={this.unitsSelectRef}
                            mode="multiple"
                            onChange={this.onConcernedUnitsChange}
                            value={this.state.concernedUnits}
                            tagRender={this.tagRender}
                            placeholder="Select FMP units"
                            options={fmpOptions}
                        />
                    </Form.Item>
                    <Form.Item className="comment" label="Comment">
                        <TextArea rows={4} onChange={v => this.setState({ comment: v.target.value })} />
                    </Form.Item>
                    <Form.Item className="response-time" label="Response time">
                        <InputTime value={this.state.responseTime} onChange={this.onResponseTimeChange} />
                    </Form.Item>
                </Form>
                <Flex className="buttons">
                    <Button onClick={this.props.onClose}>Cancel</Button>
                    <Button onClick={this.onShare} disabled={isShareDisabled}>
                        Share
                    </Button>
                </Flex>
            </Flex>
        );
    }
}
